<template>
  <v-card class="realtime-analytics-widget" :loading="loading">
    <v-card-title class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <v-icon class="me-2" color="primary">mdi-pulse</v-icon>
        <span>Analíticas en tiempo real</span>
        <v-chip v-if="stale && status === 'ready'" size="x-small" class="ms-2" color="warning" variant="outlined">stale</v-chip>
      </div>
      <v-btn icon="mdi-refresh" size="small" variant="text" @click="refresh" :loading="loading" />
    </v-card-title>

    <!--
      P13-V5A-R3 · DIRECTOR_REPAIR_R1 — four states that cannot be confused.
      Previously the ONLY non-data branch was `v-if="!metrics"` -> "Cargando métricas...", so a
      refused actor saw either an endless spinner or, once `{ denied: true }` was accepted as
      metrics, a grid of blanks stamped with an "Actualizado" time.
    -->
    <v-card-text data-testid="realtime-analytics" :data-state="status">
      <div
        v-if="status === 'unauthorized'"
        data-testid="realtime-analytics-unauthorized"
        class="text-medium-emphasis"
      >
        <v-alert type="info" variant="tonal" density="compact">
          Estas analíticas de proceso no están disponibles para su rol. No se muestran cifras: una
          denegación no se sustituye por ceros ni por datos aproximados.
        </v-alert>
      </div>
      <div
        v-else-if="status === 'error'"
        data-testid="realtime-analytics-error"
        class="text-medium-emphasis"
      >
        <v-alert type="warning" variant="tonal" density="compact">
          No se pudieron obtener las analíticas en tiempo real.
        </v-alert>
      </div>
      <div v-else-if="status === 'loading'" data-testid="realtime-analytics-loading" class="text-medium-emphasis">
        Cargando métricas...
      </div>
      <div v-else-if="status === 'empty'" data-testid="realtime-analytics-empty" class="text-medium-emphasis">
        Sin datos de telemetría disponibles.
      </div>
      <div v-else class="metrics-grid">
        <div class="metric">
          <div class="label">Eventos totales</div>
          <div class="value">{{ metrics.totalEvents }}</div>
        </div>
        <div class="metric">
          <div class="label">Hoy</div>
          <div class="value">{{ metrics.eventsToday }}</div>
        </div>
        <div class="metric">
          <div class="label">Última hora</div>
          <div class="value">{{ metrics.eventsThisHour }}</div>
        </div>
        <div class="metric">
          <div class="label">Hora anterior</div>
          <div class="value">{{ metrics.eventsLastHour }}</div>
        </div>
      </div>
      <div v-if="status === 'ready'" class="mt-4">
        <div class="subtitle">Por tipo</div>
        <div class="chips">
          <v-chip
            v-for="(val, key) in metrics.eventsByType"
            :key="key"
            size="small"
            class="ma-1"
            color="primary"
            variant="outlined"
          >{{ key }}: {{ val }}</v-chip>
        </div>
      </div>
      <div v-if="status === 'ready' && trend && trend.length" class="sparkline-wrap">
        <div class="subtitle d-flex align-center">
          Tendencia (60 min)
          <v-chip size="x-small" class="ms-2" variant="text">{{ trend[trend.length-1]?.v || 0 }}/min</v-chip>
        </div>
        <RealtimeSparkline :points="trend" :height="36" />
      </div>
      <div v-if="status === 'ready' && lastUpdated" class="text-caption text-medium-emphasis mt-4">
        Actualizado: {{ new Date(lastUpdated).toLocaleTimeString() }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useRealTimeAnalyticsStore } from '@/stores/realtime-analytics'
import RealtimeSparkline from '@/components/analytics/RealtimeSparkline.vue'

const store = useRealTimeAnalyticsStore()

const loading = computed(() => store.loading)
const metrics = computed(() => store.metrics)
const lastUpdated = computed(() => store.lastUpdated)
const stale = computed(() => store.isStale)
const status = computed(() => store.status)
const trend = computed(() => store.trend)

const refresh = () => store.refresh()

onMounted(() => {
  store.initialize()
})
</script>

<style scoped>
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.metric {
  background: rgba(0,0,0,0.03);
  border-radius: 8px;
  padding: 10px 12px;
}
.label { font-size: 12px; color: rgba(0,0,0,0.6); }
.value { font-size: 18px; font-weight: 600; }
.subtitle { font-weight: 600; margin-bottom: 6px; }
.sparkline-wrap { margin-top: 12px; }
</style>
